import type { GQL_ID } from '@@partner:types:graphql'
import type { Category, CategoryTranslation, CrudFormProps } from '@@partner:types:app'

import React, { useCallback, useEffect, useState } from 'react'
import { useFetchSingleCategory } from '@@partner:graphql/query/catalog/categoryDetails.gql'
import { useMutateSaveCategory } from '@@partner:graphql/mutation/catalog/saveCategory.gql'
import { useValidatableForm } from '@@partner:helpers/hooks/rsuite'
import { mergeLeft, mergeLeftOnly } from 'merge-left-utils'
import {
  CategoryFormState,
  makeCategoryFormState,
  mapApiDataToState,
  mapStateToApiVariable
} from './CategoryFormState'
import {
  makeGraphQLErrorNotification,
  notifyOk,
  notifyError
} from '@@partner:helpers/notification'

import { Form, Panel, Grid, Row, Col, Button, SelectPicker } from 'rsuite'
import ProductStatus from '../product/form/info/ProductStatus'
import CategoryParentSelector from './form/CategoryParentSelector'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import FormCheckbox from '@components/common/form/FormCheckbox'
import DropzoneUploader from '@components/common/form/DropzoneUploader'
import DropzonePreview from '@components/common/form/DropzonePreview'

import { CategoryFormValidationModel } from './validation/CategoryFormValidationModel'
import { SaveButton } from '../../common/form/Buttons'

import {
  API_URL_UPLOAD_CATEGORY_ICON,
  API_URL_UPLOAD_CATEGORY_ALT_ICON
} from '@@partner:config/api'

import {
  UPLOAD_NAME_CATEGORY_ICON,
  UPLOAD_NAME_CATEGORY_ALT_ICON
} from '@@partner:config/category'
import SelectLocale from '@components/common/localizedForm/SelectLocale'
import LocalizedForm from '@components/common/localizedForm/LoclalizedForm'

export interface CategoryFormProps extends CrudFormProps {
  categoryId: GQL_ID
}

const categoryPreviewSizeOptions = [
  {
    label: 'Обычный',
    value: 'normal'
  },
  {
    label: 'Двойной',
    value: 'double'
  },
  {
    label: 'Большой',
    value: 'big'
  }
]

export default function CategoryForm ({
  categoryId,
  onSaved
}: CategoryFormProps) {
  const { formState, setFormState, setFormRef } = useValidatableForm<CategoryFormState>(
    makeCategoryFormState
  )
  const [ selectedLanguage, setSelectedLanguage ] = useState<string>()
  
  const {
    data,
    loading: loadingCategory,
    error: errorLoadingCategory
  } = useFetchSingleCategory({ id: categoryId })

  const [ saveCategory, { loading: loadingSaveCategory } ] =
    useMutateSaveCategory()

  useEffect(() => {
    if (!loadingCategory && data) {
      setFormState((state) => 
        mergeLeft(state, mapApiDataToState(data.categoryDetails))
      )
    }
  }, [ data, loadingCategory, setFormState ])

  useEffect(() => {
    if (!selectedLanguage && formState.languages.length > 0) {
      setSelectedLanguage(formState.languages[0])
    }
  }, [ formState.languages ])

  const handleFormChange = useCallback(
    (formData: any) => {
      setFormState((state) => mergeLeft(state, formData))
    },
    [ setFormState ]
  )

  const handleStatusChange = useCallback(
    (status) => {
      setFormState((state) => mergeLeftOnly([ 'status' ], state, { status }))
    },
    [ setFormState ]
  )
  const handleCategoryParentChange = (parent: Category) => {
    setFormState((state) => ({ ...state, parent }))
  }


  const handleUploadIcon = useCallback((iconType: 'miniIcon' | 'icon' | 'altIcon') => (
    icon_id: GQL_ID,
    url: string
  ) => {
    const idNameMap: Record<typeof iconType, string> = {
      icon: 'icon_id',
      altIcon: 'alternative_icon_id',
      miniIcon: 'mini_icon_id'
    }
    setFormState((state) => mergeLeftOnly([ idNameMap[iconType], iconType, 'url' ], state, {
      [idNameMap[iconType]]: icon_id,
      [iconType]: { url }
    }))
  }, [ setFormState ])

  const handleUnlinkIcon = useCallback((iconType: 'miniIcon' | 'icon' | 'altIcon') => () => {
    const idNameMap: Record<typeof iconType, string> = {
      icon: 'icon_id',
      altIcon: 'alternative_icon_id',
      miniIcon: 'mini_icon_id'
    }
    setFormState((state) =>
      mergeLeftOnly([ idNameMap[iconType], iconType, 'url' ], state, makeCategoryFormState())
    )
  }, [ setFormState ])

  const handleDropParentCategoryClick = useCallback(
    () =>
      setFormState((state) =>
        mergeLeftOnly([ 'parent', 'id', 'name' ], state, {
          parent: { id: undefined, name: undefined }
        })
      ),
    [ setFormState ]
  )

  const handleTranslateChange = useCallback(
    (field: keyof CategoryTranslation) => (value: string) => {
      if (!selectedLanguage) {
        return
      }
      const newTranslations = [ ...formState.i18n ]
      const translationIndex = newTranslations.findIndex(translation => translation.language_code === selectedLanguage)
      if (translationIndex > -1) {
        newTranslations[translationIndex][field] = value
      }
      else {
        const newTranslation = mergeLeft({
          language_code: selectedLanguage,
          name: ''
        }, {
          language_code: selectedLanguage,
          [field]: value
        })
        newTranslations.push(newTranslation)
      }
      setFormState((state) => mergeLeft(state, { i18n: newTranslations }))
    },
    [ formState.i18n, selectedLanguage, setFormState ]
  )

  const handleFormSubmit = useCallback(
    (isOk = false) => {
      if (isOk) {
        saveCategory({
          variables: {
            categoryInfo: mapStateToApiVariable(formState)
          }
        })
          .then(({ data }) => {
            notifyOk('Категория сохранена')
            setFormState((state) =>
              mergeLeftOnly([ 'id' ], state, data?.category)
            )

            onSaved?.()
          })
          .catch(makeGraphQLErrorNotification('Ошибка сохранения данных'))
      }
      else {
        notifyError('Форма заполнена некорректно')
      }
    },
    [ formState, onSaved, saveCategory, setFormState ]
  )

  if (errorLoadingCategory) {
    return (
      <GraphQLCommonErrorDisplay
        title={`Не удалось загрузить категорию #${categoryId} для редактирования`}
        error={errorLoadingCategory}
      />
    )
  }

  return (
    <Form
      formValue={formState}
      onChange={handleFormChange}
      onSubmit={handleFormSubmit}
      ref={setFormRef}
      model={CategoryFormValidationModel}
      fluid
    >
      <Panel>
        <Grid fluid>
          <Row>
            <Col xs={24} md={12}>
              <Panel 
                shaded
                header='Название'
                className='my-4'
              >
                <Row className='my-4'>
                  <Form.Group>
                    <Form.ControlLabel>По-умолчанию</Form.ControlLabel>
                    <Form.Control placeholder="Название" name="name" />
                  </Form.Group>
                  <Form.ControlLabel>Перевод</Form.ControlLabel>
                  <SelectLocale
                    selectedLanguage={selectedLanguage}
                    onSelect={(language) => setSelectedLanguage(language)}
                    onAdd={(language) => {
                      setFormState((state) => mergeLeft(state, { languages: [ ...state.languages, language ] }))
                      setSelectedLanguage(language)
                    }}
                    languages={formState.languages}
                  />
                  { selectedLanguage && (
                    <LocalizedForm
                      name='name'
                      label='Название'
                      selectedLanguage={selectedLanguage}
                      value={formState.i18n?.find((translation) => translation.language_code === selectedLanguage)?.name || ''}
                      onChange={handleTranslateChange('name')}
                    />
                  )}
                </Row>
              </Panel>
              <Panel
                shaded
                header='Настройки'
                className='my-4'
              >
                <Form.Group>
                  <Form.ControlLabel>ID в ТУС</Form.ControlLabel>
                  <Form.Control placeholder="ID в ТУС" name="xid" />
                </Form.Group>
                <Form.Group>
                  <Form.ControlLabel>Имя для URL</Form.ControlLabel>
                  <Form.Control placeholder="Имя для URL" name="slug" />
                </Form.Group>
                <Form.Group>
                  <Form.ControlLabel>Статус</Form.ControlLabel>
                  <ProductStatus
                    value={formState.status}
                    onChange={handleStatusChange}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.ControlLabel>Родительская категория</Form.ControlLabel>
  
                  {formState?.parent?.id ? (
                    <>
                      <strong>Выбрано: {formState?.parent?.name}</strong>{' '}
                      <Button
                        size="xs"
                        color="red"
                        appearance="primary"
                        onClick={handleDropParentCategoryClick}
                      >
                        Удалить
                      </Button>
                    </>
                  ) : (
                    <span>Нет родительской категории</span>
                  )}
                  <CategoryParentSelector
                    skipIds={formState?.id ? [ formState.id ] : []}
                    value={formState?.parent_id}
                    onChange={handleCategoryParentChange}
                  />
                </Form.Group>
              </Panel>
              <Panel shaded header='Дополнительно' className='my-4'>
                <Form.Group>
                  <Form.ControlLabel>
                    Размер превью
                  </Form.ControlLabel>
                  <SelectPicker
                    value={formState.preview_size}
                    data={categoryPreviewSizeOptions}
                    onChange={(value) =>
                      setFormState((state) =>
                        mergeLeft(state, { preview_size: value ?? 'standard' })
                      )
                    }
                    searchable={false}
                    placeholder="Размер превью"
                    valueKey="value"
                    labelKey="label"
                    cleanable={false}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.ControlLabel>
                    Фичерная (выводить на главной)
                  </Form.ControlLabel>
                  <FormCheckbox.Control name="featured" placeholder="Фичер" />
                </Form.Group>
                <Form.Group>
                  <Form.ControlLabel>
                    Вес (чем больше - тем выше категория)
                  </Form.ControlLabel>
                  <Form.Control placeholder="Вес" name="customer_order" />
                </Form.Group>
                <Form.Group>
                  <Form.ControlLabel>
                    Вес для сборщика (чем больше - тем выше категория)
                  </Form.ControlLabel>
                  <Form.Control
                    placeholder="Вес для сборщика"
                    name="assembler_order"
                  />
                </Form.Group>
              </Panel>
              <Panel
                shaded
                header='Настройки обмена'
                className='my-4'
              >
                <Form.Group>
                  <Form.ControlLabel>Отключить обмен</Form.ControlLabel>
                  <FormCheckbox.Control
                    name="ignore_exchange_changes"
                    placeholder="Отключить обмен"
                  />
                </Form.Group>
              </Panel>
            </Col>
            <Col xs={24} md={12}>
              <Row gutter={6} className="mt-4">
                <Col sm={formState.icon?.url ? 18 : 24}>
                  <Form.Group>
                    <Form.ControlLabel>Иконка</Form.ControlLabel>
                    <DropzoneUploader
                      action={API_URL_UPLOAD_CATEGORY_ICON}
                      name={UPLOAD_NAME_CATEGORY_ICON}
                      onUploaded={handleUploadIcon('icon')}
                      multiple={false}
                    />
                  </Form.Group>
                </Col>

                {formState.icon?.url && (
                  <Col sm={6}>
                    <DropzonePreview
                      url={formState.icon.url}
                      onDelete={handleUnlinkIcon('icon')}
                    />
                  </Col>
                )}
              </Row>

              <Row>
                <Col sm={formState.altIcon?.url ? 18 : 24}>
                  <Form.Group>
                    <Form.ControlLabel>Иконка (mobile)</Form.ControlLabel>
                    <DropzoneUploader
                      action={API_URL_UPLOAD_CATEGORY_ALT_ICON}
                      name={UPLOAD_NAME_CATEGORY_ALT_ICON}
                      onUploaded={handleUploadIcon('altIcon')}
                      multiple={false}
                    />
                  </Form.Group>
                </Col>
                {formState.altIcon?.url && (
                  <Col sm={6}>
                    <DropzonePreview
                      url={formState.altIcon.url}
                      onDelete={handleUnlinkIcon('altIcon')}
                    />
                  </Col>
                )}
              </Row>
              <Row>
                <Col sm={formState.miniIcon?.url ? 18 : 24}>
                  <Form.Group>
                    <Form.ControlLabel>Мини-иконка</Form.ControlLabel>
                    <DropzoneUploader
                      action={API_URL_UPLOAD_CATEGORY_ALT_ICON}
                      name={UPLOAD_NAME_CATEGORY_ALT_ICON}
                      onUploaded={handleUploadIcon('miniIcon')}
                      multiple={false}
                    />
                  </Form.Group>
                </Col>
                {formState.miniIcon?.url && (
                  <Col sm={6}>
                    <DropzonePreview
                      url={formState.miniIcon.url}
                      onDelete={handleUnlinkIcon('miniIcon')}
                    />
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
        </Grid>
      </Panel>

      <SaveButton
        loading={loadingCategory || loadingSaveCategory}
        disabled={loadingCategory || loadingSaveCategory}
      >
        Cохранить
      </SaveButton>
    </Form>
  )
}
